import {
  ArrowLeft,
  Clock3,
  FileText,
  ImageIcon,
  PencilLine,
  Plus,
  Tag,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { useProjectShellStore } from "@/stores/projectShellStore";
import type {
  ProjectDashboardActivityItem,
  ProjectDashboardActivityKind,
} from "@/types/projects";

type SupportedLocale = "en" | "es" | "pt";

const dashboardMetricIcons: Record<
  "wordCount" | "imageCount" | "loreEntryCount" | "entityCardCount" | "timelineEventCount",
  LucideIcon
> = {
  wordCount: FileText,
  imageCount: ImageIcon,
  loreEntryCount: Tag,
  entityCardCount: Users,
  timelineEventCount: Clock3,
};

const activityKindIcons: Record<ProjectDashboardActivityKind, LucideIcon> = {
  story: FileText,
  chapter: FileText,
  lore_entry: Tag,
  entity_card: Users,
  timeline_event: Clock3,
};

const metricLabelFallbacks: Record<
  SupportedLocale,
  Record<
    "wordCount" | "imageCount" | "loreEntryCount" | "entityCardCount" | "timelineEventCount",
    string
  >
> = {
  en: {
    wordCount: "Words",
    imageCount: "Images",
    loreEntryCount: "Lore entries",
    entityCardCount: "Entity cards",
    timelineEventCount: "Timeline events",
  },
  es: {
    wordCount: "Palabras",
    imageCount: "Imagenes",
    loreEntryCount: "Entradas de lore",
    entityCardCount: "Tarjetas de entidad",
    timelineEventCount: "Eventos de la linea de tiempo",
  },
  pt: {
    wordCount: "Palavras",
    imageCount: "Imagens",
    loreEntryCount: "Entradas de lore",
    entityCardCount: "Cartoes de entidade",
    timelineEventCount: "Eventos da linha do tempo",
  },
};

const activityActionFallbacks: Record<
  SupportedLocale,
  Record<ProjectDashboardActivityItem["action"], string>
> = {
  en: {
    created: "Created",
    updated: "Edited",
  },
  es: {
    created: "Creado",
    updated: "Editado",
  },
  pt: {
    created: "Criado",
    updated: "Editado",
  },
};

const activityKindFallbacks: Record<
  SupportedLocale,
  Record<ProjectDashboardActivityKind, string>
> = {
  en: {
    story: "Story",
    chapter: "Chapter",
    lore_entry: "Lore entry",
    entity_card: "Entity",
    timeline_event: "Event",
  },
  es: {
    story: "Historia",
    chapter: "Capitulo",
    lore_entry: "Entrada de lore",
    entity_card: "Entidad",
    timeline_event: "Evento",
  },
  pt: {
    story: "Historia",
    chapter: "Capitulo",
    lore_entry: "Entrada de lore",
    entity_card: "Entidade",
    timeline_event: "Evento",
  },
};

const recentActivityTextFallbacks: Record<
  SupportedLocale,
  {
    empty: string;
    title: string;
    time: {
      daysAgo: string;
      hoursAgo: string;
      justNow: string;
      minutesAgo: string;
      weeksAgo: string;
    };
  }
> = {
  en: {
    title: "Recent activity",
    empty: "New edits and records will appear here as soon as the project changes.",
    time: {
      justNow: "Just now",
      minutesAgo: "{{count}}m ago",
      hoursAgo: "{{count}}h ago",
      daysAgo: "{{count}}d ago",
      weeksAgo: "{{count}}w ago",
    },
  },
  es: {
    title: "Actividad reciente",
    empty: "Las ediciones y registros nuevos apareceran aqui apenas cambie el proyecto.",
    time: {
      justNow: "Justo ahora",
      minutesAgo: "Hace {{count}}m",
      hoursAgo: "Hace {{count}}h",
      daysAgo: "Hace {{count}}d",
      weeksAgo: "Hace {{count}}sem",
    },
  },
  pt: {
    title: "Atividade recente",
    empty: "Novas edicoes e registros aparecem aqui assim que o projeto mudar.",
    time: {
      justNow: "Agora mesmo",
      minutesAgo: "Ha {{count}}m",
      hoursAgo: "Ha {{count}}h",
      daysAgo: "Ha {{count}}d",
      weeksAgo: "Ha {{count}}sem",
    },
  },
};

function getSupportedLocale(language: string): SupportedLocale {
  if (language.startsWith("es")) {
    return "es";
  }

  if (language.startsWith("pt")) {
    return "pt";
  }

  return "en";
}

function formatRelativeActivityTime(
  occurredAt: string,
  locale: SupportedLocale,
  t: ReturnType<typeof useTranslation>["t"],
) {
  const fallback = recentActivityTextFallbacks[locale].time;
  const diffMs = Date.now() - new Date(occurredAt).getTime();

  if (!Number.isFinite(diffMs) || diffMs < 60_000) {
    return t("projects.dashboard.recentActivity.time.justNow", {
      defaultValue: fallback.justNow,
    });
  }

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) {
    return t("projects.dashboard.recentActivity.time.minutesAgo", {
      count: minutes,
      defaultValue: fallback.minutesAgo,
    });
  }

  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) {
    return t("projects.dashboard.recentActivity.time.hoursAgo", {
      count: hours,
      defaultValue: fallback.hoursAgo,
    });
  }

  const days = Math.floor(diffMs / 86_400_000);
  if (days < 7) {
    return t("projects.dashboard.recentActivity.time.daysAgo", {
      count: days,
      defaultValue: fallback.daysAgo,
    });
  }

  return t("projects.dashboard.recentActivity.time.weeksAgo", {
    count: Math.floor(days / 7),
    defaultValue: fallback.weeksAgo,
  });
}

function getStatusClasses(status: "active" | "archived") {
  return status === "archived"
    ? "bg-slate-200/90 text-slate-700"
    : "bg-[#d8f0d3] text-[#16704b]";
}

function getActivityActionIcon(action: ProjectDashboardActivityItem["action"]) {
  return action === "created" ? Plus : PencilLine;
}

export function ProjectDashboardPage() {
  const { i18n, t } = useTranslation();
  const summary = useProjectShellStore((state) => state.currentProjectSummary);

  if (!summary) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("projects.dashboard.missing")}</p>
        </CardContent>
      </Card>
    );
  }

  const locale = getSupportedLocale(i18n.resolvedLanguage ?? i18n.language);
  const numberFormatter = new Intl.NumberFormat(i18n.language);

  const metrics = [
    {
      icon: dashboardMetricIcons.wordCount,
      label: t("projects.dashboard.countsWords", {
        defaultValue: metricLabelFallbacks[locale].wordCount,
      }),
      value: summary.wordCount,
    },
    {
      icon: dashboardMetricIcons.imageCount,
      label: t("projects.dashboard.countsImages", {
        defaultValue: metricLabelFallbacks[locale].imageCount,
      }),
      value: summary.imageCount,
    },
    {
      icon: dashboardMetricIcons.loreEntryCount,
      label: t("projects.dashboard.countsLore", {
        defaultValue: metricLabelFallbacks[locale].loreEntryCount,
      }),
      value: summary.loreEntryCount,
    },
    {
      icon: dashboardMetricIcons.entityCardCount,
      label: t("projects.dashboard.countsEntities", {
        defaultValue: metricLabelFallbacks[locale].entityCardCount,
      }),
      value: summary.entityCardCount,
    },
    {
      icon: dashboardMetricIcons.timelineEventCount,
      label: t("projects.dashboard.countsTimeline", {
        defaultValue: metricLabelFallbacks[locale].timelineEventCount,
      }),
      value: summary.timelineEventCount,
    },
  ];

  return (
    <div className="space-y-12 px-1 pb-4 pt-1">
      <section className="space-y-10">
        <Link
          className="inline-flex items-center gap-3 text-lg text-ink/72 transition hover:text-ink"
          to="/app"
        >
          <ArrowLeft className="size-5" />
          <span>{t("projects.actions.backToPortfolio")}</span>
        </Link>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">
              {t("projects.dashboard.title")}
            </p>
            <span
              className={[
                "inline-flex rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-[0.16em]",
                getStatusClasses(summary.status),
              ].join(" ")}
            >
              {summary.status === "archived"
                ? t("projects.status.archived")
                : t("projects.status.active")}
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-5xl leading-[0.95] text-ink sm:text-6xl">
              {summary.title}
            </h1>
            <p className="text-2xl text-clay">{summary.genre}</p>
          </div>
        </div>

        <div className="grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-5">
          {metrics.map((metric) => {
            const Icon = metric.icon;

            return (
              <article key={metric.label} className="space-y-3 text-center">
                <div className="flex items-center justify-center gap-3 text-[1.05rem] text-ink/72">
                  <Icon className="size-5 shrink-0" strokeWidth={1.9} />
                  <span>{metric.label}</span>
                </div>
                <p className="font-display text-5xl leading-none text-ink">
                  {numberFormatter.format(metric.value)}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="space-y-8 border-t border-[#dbcbb6] pt-10">
        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">
          {t("projects.dashboard.recentActivity.title", {
            defaultValue: recentActivityTextFallbacks[locale].title,
          })}
        </p>

        {summary.recentActivity.length ? (
          <div className="space-y-5">
            {summary.recentActivity.map((activity) => {
              const ActivityIcon = getActivityActionIcon(activity.action);
              const KindIcon = activityKindIcons[activity.kind];
              const tagLabel = t(
                `projects.dashboard.recentActivity.kinds.${activity.kind}`,
                {
                  defaultValue: activityKindFallbacks[locale][activity.kind],
                },
              );
              const tagContent = (
                <>
                  <KindIcon className="size-4" strokeWidth={1.9} />
                  <span>{activity.title}</span>
                </>
              );

              return (
                <article
                  key={`${activity.kind}:${activity.id}:${activity.occurredAt}`}
                  className="flex items-center justify-between gap-4"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#efe4d5] text-ink/74">
                      <ActivityIcon className="size-5" strokeWidth={1.9} />
                    </div>

                    <div className="flex min-w-0 flex-wrap items-center gap-3 text-lg text-ink/72">
                      <span>
                        {t(`projects.dashboard.recentActivity.actions.${activity.action}`, {
                          defaultValue: activityActionFallbacks[locale][activity.action],
                        })}
                      </span>
                      {activity.href ? (
                        <Link
                          className="inline-flex min-w-0 items-center gap-2 rounded-[0.95rem] bg-[#eee3d4] px-4 py-2 font-semibold text-ink transition hover:bg-[#e7d9c7]"
                          to={activity.href}
                        >
                          {tagContent}
                        </Link>
                      ) : (
                        <span className="inline-flex min-w-0 items-center gap-2 rounded-[0.95rem] bg-[#eee3d4] px-4 py-2 font-semibold text-ink">
                          {tagContent}
                        </span>
                      )}
                      <span className="sr-only">{tagLabel}</span>
                    </div>
                  </div>

                  <p className="shrink-0 text-lg text-ink/58">
                    {formatRelativeActivityTime(activity.occurredAt, locale, t)}
                  </p>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="text-lg text-ink/62">
            {t("projects.dashboard.recentActivity.empty", {
              defaultValue: recentActivityTextFallbacks[locale].empty,
            })}
          </p>
        )}
      </section>
    </div>
  );
}
